import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SuspensionManagementControls = ({ tenant, billingStatus, suspensionHistory, onSuspend, onReactivate }) => {
  const [showSuspendForm, setShowSuspendForm] = useState(false);
  const [showReactivateForm, setShowReactivateForm] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState('non_payment');
  const [suspensionNotes, setSuspensionNotes] = useState('');
  const [reactivationNotes, setReactivationNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const suspensionReasons = [
    { value: 'non_payment', label: 'Non-Payment', icon: 'DollarSign' },
    { value: 'policy_violation', label: 'Policy Violation', icon: 'AlertTriangle' },
    { value: 'fraud', label: 'Fraud', icon: 'Shield' },
    { value: 'voluntary', label: 'Voluntary', icon: 'Pause' },
    { value: 'other', label: 'Other', icon: 'HelpCircle' }
  ];

  const handleSuspend = async () => {
    if (!suspensionNotes?.trim()) return;
    setLoading(true);
    await onSuspend(suspensionReason, suspensionNotes);
    setLoading(false);
    setShowSuspendForm(false);
    setSuspensionNotes('');
  };

  const handleReactivate = async () => {
    if (!reactivationNotes?.trim()) return;
    setLoading(true);
    await onReactivate(reactivationNotes);
    setLoading(false);
    setShowReactivateForm(false);
    setReactivationNotes('');
  };

  const activeSuspension = suspensionHistory?.find(s => s?.is_active);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h2 className="text-lg font-heading font-bold text-foreground mb-4 flex items-center gap-2">
        <Icon name="Shield" size={20} color="var(--color-primary)" />
        Suspension Management
      </h2>

      {/* Current Status */}
      <div className={`p-4 rounded-lg mb-4 ${
        tenant?.status === 'suspended' ?'bg-destructive/10 border border-destructive' :'bg-success/10 border border-success'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">
            {tenant?.status === 'suspended' ? 'Currently Suspended' : 'Active Status'}
          </span>
          <Icon
            name={tenant?.status === 'suspended' ? 'XCircle' : 'CheckCircle'}
            size={20}
            color={tenant?.status === 'suspended' ? 'var(--color-destructive)' : 'var(--color-success)'}
          />
        </div>
        {activeSuspension && (
          <div className="text-sm text-muted-foreground">
            <p>Reason: {activeSuspension?.suspension_reason?.replace('_', ' ')}</p>
            <p>Since: {new Date(activeSuspension?.suspended_at)?.toLocaleDateString()}</p>
          </div>
        )}
      </div>

      {/* Billing Risk Indicators */}
      {billingStatus?.is_overdue && (
        <div className="p-4 bg-warning/10 border border-warning rounded-lg mb-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
            <div>
              <h4 className="font-semibold text-warning mb-1">Payment Overdue</h4>
              <p className="text-sm text-warning">
                Outstanding balance: ${parseFloat(billingStatus?.outstanding_balance || 0)?.toFixed(2)}
              </p>
              <p className="text-sm text-warning">
                Failed attempts: {billingStatus?.failed_payment_count || 0}
              </p>
              {billingStatus?.overdue_since && (
                <p className="text-sm text-warning">
                  Overdue since: {new Date(billingStatus?.overdue_since)?.toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {tenant?.status === 'active' && !showSuspendForm && (
        <Button
          variant="destructive"
          fullWidth
          onClick={() => setShowSuspendForm(true)}
        >
          <Icon name="Ban" size={16} />
          Suspend Tenant
        </Button>
      )}

      {tenant?.status === 'suspended' && !showReactivateForm && (
        <Button
          variant="default"
          fullWidth
          onClick={() => setShowReactivateForm(true)}
        >
          <Icon name="CheckCircle" size={16} />
          Reactivate Tenant
        </Button>
      )}

      {/* Suspend Form */}
      {showSuspendForm && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Reason</label>
            <div className="grid grid-cols-2 gap-2">
              {suspensionReasons?.map((reason) => (
                <button
                  key={reason?.value}
                  onClick={() => setSuspensionReason(reason?.value)}
                  className={`p-2 border rounded-lg flex items-center gap-2 text-sm transition-all ${
                    suspensionReason === reason?.value
                      ? 'border-primary bg-primary/10 text-primary' :'border-border bg-card text-foreground hover:border-primary/50'
                  }`}
                >
                  <Icon name={reason?.icon} size={16} />
                  {reason?.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Notes *</label>
            <textarea
              value={suspensionNotes}
              onChange={(e) => setSuspensionNotes(e?.target?.value)}
              placeholder="Provide detailed reason..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              fullWidth
              onClick={() => {
                setShowSuspendForm(false);
                setSuspensionNotes('');
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              fullWidth
              onClick={handleSuspend}
              disabled={loading || !suspensionNotes?.trim()}
            >
              {loading ? 'Suspending...' : 'Confirm Suspension'}
            </Button>
          </div>
        </div>
      )}

      {/* Reactivate Form */}
      {showReactivateForm && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Reactivation Notes *</label>
            <textarea
              value={reactivationNotes}
              onChange={(e) => setReactivationNotes(e?.target?.value)}
              placeholder="Provide reason for reactivation..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              fullWidth
              onClick={() => {
                setShowReactivateForm(false);
                setReactivationNotes('');
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              fullWidth
              onClick={handleReactivate}
              disabled={loading || !reactivationNotes?.trim()}
            >
              {loading ? 'Reactivating...' : 'Confirm Reactivation'}
            </Button>
          </div>
        </div>
      )}

      {/* Suspension History */}
      {suspensionHistory?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-foreground mb-3">Suspension History</h3>
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {suspensionHistory?.map((suspension) => (
              <div key={suspension?.id} className="p-3 bg-muted/30 rounded-lg text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-foreground">
                    {suspension?.suspension_reason?.replace('_', ' ')}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    suspension?.is_active
                      ? 'bg-destructive/10 text-destructive' :'bg-success/10 text-success'
                  }`}>
                    {suspension?.is_active ? 'Active' : 'Resolved'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {new Date(suspension?.suspended_at)?.toLocaleDateString()}
                  {suspension?.reactivated_at && ` - ${new Date(suspension?.reactivated_at)?.toLocaleDateString()}`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuspensionManagementControls;